import React, { useState, useRef } from 'react';
import api from '../../../../config/axios';
import Swal from 'sweetalert2';
import { FaUpload, FaTimes, FaFileAlt } from 'react-icons/fa';

const ImportarArquivo = ({ onClose, onImportacaoSalva, contasBancarias }) => {
    const [arquivo, setArquivo] = useState(null);
    const [contaBancariaId, setContaBancariaId] = useState('');
    const [preview, setPreview] = useState(null);
    const fileInputRef = useRef(null);
    const abortControllerRef = useRef(null);

    const handleArquivoChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const allowedExtensions = ['.pdf', '.ofx', '.csv', '.txt'];
            const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));

            if (!allowedExtensions.includes(ext)) {
                Swal.fire('Erro', 'Tipo de arquivo não suportado. Use PDF, OFX, CSV ou TXT.', 'error');
                return;
            }

            if (file.size > 10 * 1024 * 1024) {
                Swal.fire('Erro', 'O arquivo deve ter no máximo 10MB.', 'error');
                return;
            }

            setArquivo(file);
            setPreview(null);
            await carregarPreview(file);
        }
    };

    const carregarPreview = async (file) => {
        abortControllerRef.current = new AbortController();

        Swal.fire({
            title: 'Analisando arquivo...',
            html: 'Aguarde enquanto o arquivo é processado.',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showCancelButton: true,
            cancelButtonText: 'Cancelar',
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            },
            willClose: () => {
                if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                }
            }
        }).then((result) => {
            if (result.dismiss === Swal.DismissReason.cancel) {
                setArquivo(null);
                setPreview(null);
            }
        });

        try {
            const formData = new FormData();
            formData.append('arquivo', file);

            const response = await api.post('/conciliacao/preview', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                signal: abortControllerRef.current.signal
            });

            Swal.close();
            setPreview(response.data);
        } catch (error) {
            if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
                return;
            }
            Swal.fire('Erro', error.response?.data?.message || 'Erro ao analisar arquivo', 'error');
            setArquivo(null);
            setPreview(null);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const file = e.dataTransfer.files[0];
        if (file) {
            const allowedExtensions = ['.pdf', '.ofx', '.csv', '.txt'];
            const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));

            if (!allowedExtensions.includes(ext)) {
                Swal.fire('Erro', 'Tipo de arquivo não suportado. Use PDF, OFX, CSV ou TXT.', 'error');
                return;
            }

            setArquivo(file);
            setPreview(null);
            await carregarPreview(file);
        }
    };

    const executarImportacao = async (forcarImportacao = false) => {
        Swal.fire({
            title: 'Importando...',
            html: 'Processando o arquivo...',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        const formData = new FormData();
        formData.append('arquivo', arquivo);
        formData.append('conta_bancaria_id', contaBancariaId);
        if (forcarImportacao) {
            formData.append('forcar_importacao', 'true');
        }
        if (preview?.layout_id) {
            formData.append('layout_id', preview.layout_id);
        }

        const response = await api.post('/conciliacao/importar', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        return response;
    };

    const importarComForca = async () => {
        try {
            const response = await executarImportacao(true);
            exibirResultadoSucesso(response.data);
        } catch (error) {
            Swal.fire('Erro', error.response?.data?.message || 'Erro ao importar arquivo', 'error');
        }
    };

    const exibirResultadoSucesso = (resultado) => {
        Swal.close();

        const contaSelecionada = contasBancarias.find(c => c.id === parseInt(contaBancariaId));
        const importacaoCriada = {
            id: resultado.importacao.id,
            nome_arquivo: resultado.importacao.nome_arquivo,
            conta_bancaria_id: parseInt(contaBancariaId),
            contaBancaria: contaSelecionada,
            total_transacoes: resultado.importacao.total_transacoes,
            transacoes_conciliadas: resultado.estatisticas?.conciliadas_automatico || 0,
            periodo_inicio: resultado.cabecalho?.periodo?.inicio,
            periodo_fim: resultado.cabecalho?.periodo?.fim,
            banco: resultado.cabecalho?.banco || resultado.importacao.layout_detectado
        };
        onImportacaoSalva(importacaoCriada);
        onClose();
    };

    const handleSubmit = async () => {
        if (!arquivo) {
            Swal.fire('Erro', 'Selecione um arquivo para importar', 'error');
            return;
        }

        if (!contaBancariaId) {
            Swal.fire('Erro', 'Selecione uma conta bancária', 'error');
            return;
        }

        try {
            const response = await executarImportacao(false);
            exibirResultadoSucesso(response.data);
        } catch (error) {
            if (error.response?.data?.duplicado) {
                const { existente } = error.response.data;
                Swal.fire({
                    title: 'Arquivo já Importado',
                    html: `
                        <p>Este arquivo já foi importado anteriormente.</p>
                        <div class="alert alert-warning mb-0 mt-3">
                            <p class="mb-1"><strong>Data:</strong> ${new Date(existente.created_at).toLocaleDateString('pt-BR')} ${new Date(existente.created_at).toLocaleTimeString('pt-BR')}</p>
                            <p class="mb-1"><strong>Conta:</strong> ${existente.contaBancaria?.nome_exibicao || existente.contaBancaria?.nome}</p>
                            <p class="mb-0"><strong>Status:</strong> ${existente.status ? existente.status.charAt(0).toUpperCase() + existente.status.slice(1) : existente.status}</p>
                        </div>
                    `,
                    icon: 'warning'
                });
            } else if (error.response?.data?.periodo_duplicado) {
                const { importacao_existente, periodo_novo } = error.response.data;
                const result = await Swal.fire({
                    title: 'Período Sobreposto',
                    html: `
                        <p>Já existe uma importação com período sobreposto nesta conta.</p>
                        <div class="alert alert-warning mb-0 mt-3">
                            <p class="mb-1"><strong>Arquivo existente:</strong> ${importacao_existente?.nome_arquivo || '-'}</p>
                            <p class="mb-1"><strong>Período existente:</strong> ${importacao_existente?.periodo?.inicio ? formatarData(importacao_existente.periodo.inicio) : '-'} a ${importacao_existente?.periodo?.fim ? formatarData(importacao_existente.periodo.fim) : '-'}</p>
                            <p class="mb-0"><strong>Status:</strong> ${importacao_existente?.status ? importacao_existente.status.charAt(0).toUpperCase() + importacao_existente.status.slice(1) : '-'}</p>
                            <hr class="my-2">
                            <p class="mb-0 mt-3"><strong>Período do novo arquivo:</strong> ${periodo_novo?.inicio ? formatarData(periodo_novo.inicio) : '-'} a ${periodo_novo?.fim ? formatarData(periodo_novo.fim) : '-'}</p>
                        </div>
                        <p class="mt-3 mb-0">Deseja importar mesmo assim?</p>
                    `,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Sim, importar',
                    cancelButtonText: 'Cancelar'
                });

                if (result.isConfirmed) {
                    await importarComForca();
                }
            } else {
                Swal.fire('Erro', error.response?.data?.message || 'Erro ao importar arquivo', 'error');
            }
        }
    };

    const formatarValor = (valor) => {
        if (!valor) return 'R$ 0,00';
        return parseFloat(valor).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        });
    };

    const formatarData = (dataString) => {
        if (!dataString) return '-';
        if (dataString.includes('-')) {
            const [ano, mes, dia] = dataString.split('-');
            return `${dia}/${mes}/${ano}`;
        }
        return dataString;
    };

    return (
        <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h4 className="fw-bold mt-2">Importar Arquivo</h4>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <div className="row">
                            <div className="col-12 mb-3">
                                <label className="form-label fw-bold">Conta Bancária</label>
                                <select
                                    className="form-select"
                                    value={contaBancariaId}
                                    onChange={(e) => setContaBancariaId(e.target.value)}
                                >
                                    <option value="">Selecione a conta...</option>
                                    {contasBancarias.map(conta => (
                                        <option key={conta.id} value={conta.id}>
                                            {conta.nome_exibicao || conta.nome}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {contaBancariaId && (
                                <div className="col-12 mb-3">
                                    <label className="form-label fw-bold">Banco</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={contasBancarias.find(c => c.id === parseInt(contaBancariaId))?.banco?.nome || '-'}
                                        disabled
                                    />
                                </div>
                            )}
                        </div>

                        <div className="row">
                            <div className="col-12">
                                <label className="form-label fw-bold">Arquivo (PDF, OFX, CSV ou TXT)</label>
                                <div
                                    className={`border rounded p-4 text-center ${arquivo ? 'border-success' : 'border-secondary'}`}
                                    style={{ borderStyle: 'dashed', cursor: 'pointer' }}
                                    onClick={() => fileInputRef.current?.click()}
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop}
                                >
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        accept=".pdf,.ofx,.csv,.txt"
                                        onChange={handleArquivoChange}
                                        style={{ display: 'none' }}
                                    />

                                    {arquivo ? (
                                        <div>
                                            <FaFileAlt size={40} className="text-success mb-2" />
                                            <p className="mb-1 fw-bold">{arquivo.name}</p>
                                            <p className="text-muted mb-0">
                                                {(arquivo.size / 1024).toFixed(2)} KB
                                            </p>
                                        </div>
                                    ) : (
                                        <div>
                                            <FaUpload size={40} className="text-muted mb-2" />
                                            <p className="mb-1">Clique para selecionar ou arraste o arquivo aqui</p>
                                            <p className="text-muted mb-0">PDF, OFX, CSV ou TXT (máx. 10MB)</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {preview && (
                            <div className="row mt-3">
                                <div className="col-12">
                                    <div className="card info-card">
                                        <div className="card-body p-3">
                                            <h6 className="fw-bold mb-3">Preview do Arquivo</h6>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <p className="mb-1">
                                                        <strong>Transações:</strong> {preview.total_transacoes || preview.transacoes_preview?.length || 0}
                                                    </p>
                                                </div>
                                                <div className="col-md-6">
                                                    {preview.periodo && (
                                                        <p className="mb-1">
                                                            <strong>Período:</strong> {formatarData(preview.periodo.inicio)} a {formatarData(preview.periodo.fim)}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-danger" onClick={onClose}>
                            <FaTimes className="me-1" />
                            Fechar
                        </button>
                        <button
                            type="button"
                            className="btn btn-custom-primary"
                            onClick={handleSubmit}
                            disabled={!arquivo || !contaBancariaId}
                        >
                            <FaUpload className="me-1" />
                            Importar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImportarArquivo;
